import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdModalFooterButton } from '@commander-services/gui-components';
import CmdModal from '@commander-services/cmd-modal';
import CmdSimpleSelect from '@commander-services/cmd-simple-select';
import CmdInput from '@commander-services/cmd-input';
import CmdLabel from '@commander-services/cmd-label';
import CmdRadioButton from '@commander-services/cmd-radio-button';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Controller, useForm } from 'react-hook-form';
import { AxiosResponse } from 'axios';
import PermissionsService, {
  PERMISSION_VEHICLE_GROUP_CUSTOMER,
  PERMISSION_VEHICLE_GROUP_PRIVATE,
} from '../../../Services/PermissionsService';

import FormikValidatorService, {
  IValidationApiResponse,
} from '../../../Services/FormikValidatorService';
import usePageWithForm from '../../../hooks/usePageWithForm';
import { transformErrors, validateFields } from '../../../Services/HookFormService';
import { IVehicleGroup } from '../../Vehicles/types';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import { vehicleGroupsAtom } from '../../../store/recoil/vehicles';
import { postRequest, putRequest } from '../../../Services/HttpService';
import useAnalytics from '../../../hooks/useAnalytics';

const RESPONSE_OK = 'ok';

interface ModalFormProps {
  handleClose: any;
  editingValues: any;
  getFilteredVehiclesGroups: any;
  setGroups: any;
  groups: any;
  customers: any;
}

interface ICarsGroupFormValues {
  name: string;
  type: string;
  customerId: string;
}

export default function ModalForm(props: ModalFormProps): JSX.Element {
  usePageWithForm();
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const { formatMessage: f } = useIntl();
  const { customers, editingValues } = props;
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const setVehicleGroups = useSetRecoilState<IVehicleGroup[]>(vehicleGroupsAtom);
  const { trackEvent } = useAnalytics();

  const formValidation: IValidationApiResponse | null = {
    name: [
      {
        param: 'minLength',
        value: 3,
        message: f({ id: 'carsGroups.title.rule.minLength' }, { _count: 3 }),
      },
      {
        param: 'maxLength',
        value: 150,
        message: f({ id: 'carsGroups.title.rule.maxLength' }, { count: 150 }),
      },
      { param: 'required', value: true, message: f({ id: 'form.required' }) },
    ],
    type: [{ param: 'required', value: true, message: f({ id: 'form.required' }) }],
    customerId: [],
  };

  let selectedOption = 'public';
  let disabled = false;

  if (Object.keys(editingValues).length === 0) {
    const allAllowedCreate =
      PermissionsService.hasAccess(PERMISSION_VEHICLE_GROUP_CUSTOMER, ['create']) &&
      PermissionsService.hasAccess(PERMISSION_VEHICLE_GROUP_PRIVATE, ['create']);
    if (
      !allAllowedCreate &&
      PermissionsService.hasAccess(PERMISSION_VEHICLE_GROUP_PRIVATE, ['create'])
    ) {
      selectedOption = 'private';
      disabled = true;
    } else if (
      !allAllowedCreate &&
      PermissionsService.hasAccess(PERMISSION_VEHICLE_GROUP_CUSTOMER, ['create'])
    ) {
      disabled = true;
    }
  }

  let customersOptions: any = [];
  if (customers) {
    if (Object.keys(editingValues).length > 0) {
      customersOptions = Object.values(customers)
        .filter(
          (i: any) =>
            selectedCustomers.includes(i.id) &&
            (PermissionsService.hasAccess(PERMISSION_VEHICLE_GROUP_CUSTOMER, ['update'], i.id) ||
              PermissionsService.hasAccess(PERMISSION_VEHICLE_GROUP_PRIVATE, ['update'], i.id))
        )
        .map((i: any) => {
          return { value: i.id, item: i.name };
        });
    } else {
      customersOptions = Object.values(customers)
        .filter(
          (i: any) =>
            selectedCustomers.includes(i.id) &&
            (PermissionsService.hasAccess(PERMISSION_VEHICLE_GROUP_CUSTOMER, ['create'], i.id) ||
              PermissionsService.hasAccess(PERMISSION_VEHICLE_GROUP_PRIVATE, ['create'], i.id))
        )
        .map((i: any) => {
          return { value: i.id, item: i.name };
        });
    }
  }
  const {
    handleSubmit,
    setError,
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm<ICarsGroupFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: editingValues.name ? editingValues.name : '',
      type: editingValues.type ? editingValues.type : selectedOption,
      customerId:
        Object.keys(editingValues).length > 0
          ? editingValues.customerId
          : customersOptions && customersOptions.length > 0
            ? customersOptions[0].value
            : null,
    },
    resolver: async (data) => {
      const validateErros = validateFields(data, formValidation);
      const formErrors = transformErrors(validateErros);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const getLabel = (options: ISimpleSelectOptions[], value: string) => {
    const option = options.find((i) => String(value) === String(i.value));
    if (option) {
      return option.item;
    }
    return '';
  };

  const saveVehicleGroup = async (values) => {
    setIsLoaderActive(true);
    if (values.type !== 'public') {
      delete values.customerId;
    }
    const response: AxiosResponse | false = await postRequest('/v2/vehicle-group/add', values, {
      returnAxiosResponse: true,
    });
    setIsLoaderActive(false);
    if (response && response.data && response.data.status === 'ok') {
      props.getFilteredVehiclesGroups('');
      props.handleClose();
    }
  };

  const addVehicleGroup = (values) => {
    if (values.type === 'public' && !values.customerId) {
      setError('customerId', { message: f({ id: 'form.required' }) });
      return false;
    }
    saveVehicleGroup(values);
  };

  const editVehicleGroup = async (values) => {
    const { groups } = props;
    const { id } = props.editingValues;
    setIsLoaderActive(true);
    if (values.type !== 'public') {
      delete values.customerId;
    }
    const response: AxiosResponse | false = await putRequest(
      `/v2/vehicle-group/${id}/edit`,
      values,
      { returnAxiosResponse: true }
    );
    setIsLoaderActive(false);
    if (response && response.data && response.data.status === RESPONSE_OK) {
      props.handleClose();
      const editingItem = response.data.vehicleGroup;
      const data: any = [];
      groups.forEach((item: any) => {
        if (item.id === editingItem.id) {
          data.push(editingItem);
        } else {
          data.push(item);
        }
      });
      props.setGroups(data);
      setVehicleGroups(data);
    }
  };

  const radioValues = [
    {
      value: 'public',
      item: f({ id: 'carsGroups.customer' }),
    },
    {
      value: 'private',
      item: f({ id: 'carsGroups.user' }),
    },
  ];

  const handleRadioValues = (
    optionsRadio: { value: string; item: string }[],
    customerId?: number
  ): { value: string; item: string }[] => {
    if (customerId) {
      const tempOptions = optionsRadio.filter(
        (item) =>
          (item.value === 'public' &&
            PermissionsService.hasAccess(
              PERMISSION_VEHICLE_GROUP_CUSTOMER,
              ['create', 'update'],
              customerId
            )) ||
          (item.value === 'private' &&
            PermissionsService.hasAccess(
              PERMISSION_VEHICLE_GROUP_PRIVATE,
              ['create', 'update'],
              customerId
            ))
      );
      return tempOptions;
    }
    return optionsRadio;
  };

  const getCustomerSelectPermission = (type?: string) => {
    if (Object.keys(editingValues).length > 0) {
      return type === 'public';
    }
    return type === 'public' && selectedOption === 'public';
  };

  const buttons: CmdModalFooterButton[] =
    Object.keys(editingValues).length > 0
      ? [
          {
            id: 'modalClose',
            type: 'button',
            title: f({ id: 'form.close' }),
            className: 'e-button',
            closeCallback: props.handleClose,
          },
          {
            id: 'submitEditModal',
            type: 'submit',
            title: f({ id: 'carsGroups.editBtn' }),
            className: 'e-button e-button--gray',
            submitCallback: handleSubmit(
              Object.keys(editingValues).length > 0 ? editVehicleGroup : addVehicleGroup
            ),
          },
        ]
      : [
          {
            id: 'closeCarGroupsModal',
            type: 'button',
            title: f({ id: 'form.close' }),
            className: 'e-button',
            closeCallback: props.handleClose,
          },
          {
            id: 'submitAddForm',
            type: 'submit',
            title: f({ id: 'carsGroups.addBtn' }),
            className: 'e-button e-button--gray',
            submitCallback: handleSubmit(
              Object.keys(editingValues).length > 0 ? editVehicleGroup : addVehicleGroup
            ),
          },
        ];

  return (
    <CmdModal
      title={
        Object.keys(editingValues).length > 0
          ? f({ id: 'carsGroups.editGroup' })
          : f({ id: 'carsGroups.addGroup' })
      }
      footerButtons={buttons}
      // loader={isLoaderActive}
    >
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <CmdInput
            {...field}
            label={f({ id: 'carsGroups.groupTitle' })}
            id="name"
            type="text"
            onChangeCallback={(value) => {
              setValue('name', value);
              trigger('name');
            }}
            required={FormikValidatorService.isFieldRequired(formValidation.name)}
            error={errors.name?.message}
            newStyle
          />
        )}
      />
      {editingValues.type || disabled ? (
        <CmdLabel
          title={f({ id: 'carsGroups.groupType' })}
          value={getLabel(radioValues, watch('type'))}
        />
      ) : (
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <CmdRadioButton
              {...field}
              label={f({ id: 'carsGroups.groupType' })}
              id="type"
              options={handleRadioValues(
                radioValues,
                watch('customerId') ? Number(watch('customerId')) : undefined
              )}
              // disabled={editingValues.type || disabled}
              onChangeCallback={(value) => {
                setValue('type', value);
                trigger('type');
                trackEvent(['cars groups form', 'change', 'Change type']);
              }}
              required={FormikValidatorService.isFieldRequired(formValidation.type)}
              error={errors.type?.message}
            />
          )}
        />
      )}
      {/* {getCustomerSelectPermission(watch('type')) && ( */}
      <div>
        {editingValues.type && editingValues.type === 'public' && (
          <CmdLabel
            title={f({ id: 'table_list.columns.onlineMapTemplates.assign.customerId' })}
            value={getLabel(customersOptions, watch('customerId'))}
          />
        )}
        {!editingValues.type && (
          <Controller
            name="customerId"
            control={control}
            render={({ field }) => (
              <CmdSimpleSelect
                {...field}
                label={f({ id: 'table_list.columns.onlineMapTemplates.assign.customerId' })}
                id="customerId"
                disabled={!getCustomerSelectPermission(watch('type'))}
                options={customersOptions}
                // disabled={editingValues.type && editingValues.type === 'public'}
                placeholder={f({ id: 'form.choose' })}
                onChangeCallback={(value) => {
                  setValue('customerId', value);
                  trigger('customerId');
                  trackEvent(['cars groups form', 'change', 'Change customer']);
                }}
                required={FormikValidatorService.isFieldRequired(formValidation.customerId)}
                error={errors.customerId?.message}
              />
            )}
          />
        )}
      </div>
    </CmdModal>
  );
}
